import {Button, DatePicker, message, Table} from "antd";
import dayjs from "dayjs";
import React, {useEffect, useState} from "react";
import type {ColumnsType} from "antd/es/table";
import "../../settings/Settings.css";
import {DownloadOutlined} from "@ant-design/icons";
import {HttpService} from "../../../services/http/Http.service";
import {HttpErrorService} from "../../../services/http/HttpError.service";
import {UtilsService} from "../../../services/Utils.service";
import {PracticeExportService} from "../../practices/PracticeExport.service";
import {CallCountRow} from "../../../model/models";

type FilterType = "today" | "week" | "month" | "year" | "custom";

export default function ReportChiamate() {
    const [data, setData] = useState<CallCountRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [activeFilter, setActiveFilter] = useState<FilterType>("today");
    const [customFrom, setCustomFrom] = useState<string | null>(null);
    const [customTo, setCustomTo] = useState<string | null>(null);
    const [reportRange, setReportRange] = useState<{ from: string, to: string } | null>(null);
    const [filterCounts, setFilterCounts] = useState<Record<FilterType, number>>({
        today: 0,
        week: 0,
        month: 0,
        year: 0,
        custom: 0,
    });


    useEffect(() => {
        fetchCounts();
        handlePresetFilter("today");
    }, []);

    const fetchCounts = async () => {
        try {
            const res = await HttpService.getInstanceAxios().get("/registry/report/standardCounts");
            if (res.data.custom === null)
                res.data.custom = "-"
            setFilterCounts(res.data);
        } catch (error) {
            HttpErrorService.handle("Errore durante il recupero dei contatori delle chiamate", error, true)
        }

    };

    useEffect(() => {
        if (activeFilter !== "custom" || !customFrom || !customTo) return;

        HttpService.getInstanceAxios()
            .get(`/registry/report/customCounts?from=${customFrom}&to=${customTo}`)
            .then(res => {
                setFilterCounts(prev => ({
                    ...prev,
                    custom: res.data
                }));
            });
    }, [customFrom, customTo]);

    const fetchData = async (from: string, to: string) => {
        setLoading(true);
        setData([]);
        try {
            if (from !== "" && to !== "") {
                const res = await HttpService.getInstanceAxios().get(`/registry/report?from=${from}&to=${to}`);
                setData(res.data);
            }
        } catch (error: any) {
            HttpErrorService.handle("Errore durante il recupero dei dati", error, true);
        } finally {
            setLoading(false);
        }
    };

    const handlePresetFilter = (type: FilterType) => {
        const to = dayjs().format("YYYY-MM-DD");
        let from = to;

        switch (type) {
            case "today":
                from = to;
                break;
            case "week":
                from = dayjs().subtract(7, "day").format("YYYY-MM-DD");
                break;
            case "month":
                from = dayjs().subtract(1, "month").format("YYYY-MM-DD");
                break;
            case "year":
                from = dayjs().subtract(1, "year").format("YYYY-MM-DD");
                break;
            case "custom":
                break;
        }

        setActiveFilter(type);
        const range = {from, to};
        setReportRange(range);
        fetchData(range.from, range.to);
    };

    useEffect(() => {
        if (activeFilter === "custom") {
            const from = customFrom || dayjs().subtract(20, "year").format("YYYY-MM-DD");
            const to = customTo || dayjs().add(1, "day").format("YYYY-MM-DD");

            setReportRange({from, to});
            setData([]);
            HttpService.getInstanceAxios()
                .get(`/registry/report?from=${from}&to=${to}`)
                .then((res) => {
                    setData(res.data);
                    setFilterCounts(prev => ({
                        ...prev,
                        custom: res.data.length
                    }));
                })
                .catch(error => {
                    setFilterCounts(prev => ({
                        ...prev,
                        custom: 0
                    }));
                    HttpErrorService.handle("Errore durante il recupero dei dati personalizzati", error, true);
                });
        }
    }, [customFrom, customTo]);

    const columns: ColumnsType<CallCountRow> = [
        {
            title: "Dipendente ATS",
            dataIndex: "operatorName",
            key: "operatorName",
        },
        {
            title: "Data e ora della telefonata",
            dataIndex: "callDateTime",
            key: "callDateTime",
            render: (value: string) => UtilsService.formatDateHour(value),
        },
        {
            title: "Stato della richiesta",
            dataIndex: "isDone",
            key: "isDone",
            render: (value: boolean) => value ? "Evasa" : "Da evadere",
        },
        {
            title: "Codice fiscale assistito",
            dataIndex: "userFiscalCode",
            key: "userFiscalCode",
        },
        {
            title: "Nome e cognome assistito",
            key: "userFullName",
            render: (_, record) => `${record.userFirstName} ${record.userLastName}`,
        },
    ];

    const isActive = (type: FilterType) => activeFilter === type;

    return (
        <>
            <div className="report-header">
                <div className="report-filters">
                    {["today", "week", "month", "year", "custom"].map((type) => (
                        <Button
                            key={type}
                            className={`report-filter-button ${isActive(type as FilterType) ? "active" : ""}`}
                            onClick={() => {
                                if (type === "custom") {
                                    setActiveFilter("custom");
                                    setCustomFrom(null);
                                    setCustomTo(null);
                                    const defaultFrom = dayjs().subtract(20, "year").format("YYYY-MM-DD");
                                    const defaultTo = dayjs().format("YYYY-MM-DD");
                                    setReportRange({from: defaultFrom, to: defaultTo});
                                    fetchData(defaultFrom, defaultTo);
                                } else {
                                    handlePresetFilter(type as FilterType);
                                }
                            }}
                        >
                            {type === "today" && `Oggi (${filterCounts.today})`}
                            {type === "week" && `Ultima settimana (${filterCounts.week})`}
                            {type === "month" && `Ultimo mese (${filterCounts.month})`}
                            {type === "year" && `Ultimo anno (${filterCounts.year})`}
                            {type === "custom" && `Personalizzato (${filterCounts.custom})`}
                        </Button>
                    ))}

                    <DatePicker
                        placeholder="Da"
                        format={"DD-MM-YYYY"}
                        value={customFrom ? dayjs(customFrom) : null}
                        onChange={(d) => setCustomFrom(d ? d.format("YYYY-MM-DD") : null)}
                        disabled={activeFilter !== "custom"}
                    />
                    <DatePicker
                        placeholder="A"
                        format={"DD-MM-YYYY"}
                        value={customTo ? dayjs(customTo) : null}
                        onChange={(d) => setCustomTo(d ? d.format("YYYY-MM-DD") : null)}
                        disabled={activeFilter !== "custom"}
                    />
                    <Button
                        type="primary"
                        icon={<DownloadOutlined/>}
                        onClick={() => {
                            if (!reportRange || data.length === 0) {
                                message.error("Nessun dato disponibile da esportare.");
                                return;
                            }
                            PracticeExportService.loadAndExportToExcelReport(reportRange.from, reportRange.to);
                        }}
                    >
                        Genera report
                    </Button>
                </div>
            </div>
            <Table
                rowKey="id"
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={false}
                size="small"
            />
        </>
    );
}
